import api from './api';

/**
 * Obtiene productos con filtros y paginación
 * @param {Object} filters - Filtros y opciones de paginación/ordenamiento
 * @returns {Promise} - Promesa con los productos
 */
export const getProducts = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Añadir todos los parámetros a la URL
        Object.entries(filters).forEach(([key, value]) => {
            // Manejar valores especiales para country y city
            if (key === 'country' && value === 'none') {
                console.log('Enviando filtro para país no definido');
                // El valor 'none' se pasa directamente para que el backend lo procese
                queryParams.append(key, value);
            } else if (key === 'city' && value === 'none') {
                console.log('Enviando filtro para ciudad no definida');
                // El valor 'none' se pasa directamente para que el backend lo procese
                queryParams.append(key, value);
            } else if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });
        
        const response = await api.get(`/api/products?${queryParams}`);
        return response.data;
    } catch (error) {
        console.error('Error en servicio getProducts:', error);
        throw error;
    }
};

/**
 * Obtiene un producto por ID
 * @param {Number} id - ID del producto
 * @returns {Promise} - Promesa con el producto
 */
export const getProductById = async (id) => {
    try {
        const response = await api.get(`/api/products/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio getProductById para id ${id}:`, error);
        throw error;
    }
};

/**
 * Crea un nuevo producto
 * @param {Object} productData - Datos del nuevo producto
 * @returns {Promise} - Promesa con el producto creado
 */
export const createProduct = async (productData) => {
    try {
        const response = await api.post('/api/products', productData);
        return response.data;
    } catch (error) {
        console.error('Error en servicio createProduct:', error);
        throw error;
    }
};

/**
 * Actualiza un producto existente
 * @param {Number} id - ID del producto a actualizar
 * @param {Object} productData - Datos actualizados del producto
 * @returns {Promise} - Promesa con el producto actualizado
 */
export const updateProduct = async (id, productData) => {
    try {
        const response = await api.put(`/api/products/${id}`, productData);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio updateProduct para id ${id}:`, error);
        throw error;
    }
};

/**
 * Elimina un producto
 * @param {Number} id - ID del producto a eliminar
 * @returns {Promise} - Promesa con el resultado
 */
export const deleteProduct = async (id) => {
    try {
        const response = await api.delete(`/api/products/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio deleteProduct para id ${id}:`, error);
        throw error;
    }
};

/**
 * Elimina un producto junto con sus ventas asociadas
 * @param {Number} id - ID del producto a eliminar
 * @returns {Promise} - Promesa con el resultado
 */
export const deleteProductWithSales = async (id) => {
    try {
        const response = await api.delete(`/api/products/${id}/with-sales`);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio deleteProductWithSales para id ${id}:`, error);
        throw error;
    }
};

/**
 * Verifica si un producto tiene ventas asociadas
 * @param {Number} id - ID del producto a verificar
 * @returns {Promise} - Promesa con la información de ventas
 */
export const checkProductSales = async (id) => {
    try {
        const response = await api.get(`/api/products/${id}/check-sales`);
        return response.data;
    } catch (error) {
        console.error(`Error en servicio checkProductSales para id ${id}:`, error);
        throw error;
    }
};

/**
 * Elimina todos los productos que no tienen ventas asociadas
 * @returns {Promise} - Promesa con el resultado incluyendo el número de productos eliminados
 */
export const deleteProductsWithoutSales = async () => {
    try {
        // Usar POST para operaciones que no encajan en el CRUD estándar
        const response = await api.post('/api/products/bulk-actions', {
            action: 'deleteWithoutSales'
        });
        return response.data;
    } catch (error) {
        console.error('Error en servicio deleteProductsWithoutSales:', error);
        throw error;
    }
};

/**
 * Actualiza múltiples productos en masa (país y/o ciudad)
 * @param {Array} productIds - Array con los IDs de los productos a actualizar
 * @param {Object} updateData - Datos a actualizar (country y/o city)
 * @returns {Promise} - Promesa con el resultado de la actualización
 */
export const updateProductsBulk = async (productIds, updateData) => {
    try {
        const response = await api.post('/api/products/bulk-actions', {
            action: 'updateBulk',
            productIds,
            ...updateData
        });
        return response.data;
    } catch (error) {
        console.error('Error en servicio updateProductsBulk:', error);
        throw error;
    }
};

/**
 * Obtener estadísticas de un producto
 * @param {Number} id - ID del producto
 * @param {String} timeRange - Rango de tiempo para las estadísticas
 * @param {String} startDate - Fecha de inicio (para rango personalizado)
 * @param {String} endDate - Fecha de fin (para rango personalizado)
 * @returns {Promise} - Promesa con las estadísticas del producto
 */
export const getProductStats = async (id, timeRange = 'week', startDate = null, endDate = null) => {
    try {
        const params = { timeRange };
        
        // Añadir fechas personalizadas si se proporcionan
        if (timeRange === 'custom' && startDate && endDate) {
            params.startDate = startDate;
            params.endDate = endDate;
        }
        
        const response = await api.get(`/api/products/${id}/stats`, {
            params
        });
        return response.data;
    } catch (error) {
        console.error(`Error en servicio getProductStats para id ${id}:`, error);
        throw error;
    }
};

/**
 * Obtener estadísticas generales de productos
 * @param {String} timeRange - Rango de tiempo para las estadísticas
 * @returns {Promise} - Promesa con las estadísticas generales de productos
 */
export const getProductsStats = async (timeRange = 'week') => {
    try {
        const response = await api.get(`/api/products/stats`, {
            params: { timeRange }
        });
        return response.data;
    } catch (error) {
        console.error('Error en servicio getProductsStats:', error);
        throw error;
    }
};

export const productService = {
    getProducts,
    getProductById,
    createProduct,
    updateProduct,
    deleteProduct,
    deleteProductWithSales,
    checkProductSales,
    deleteProductsWithoutSales,
    updateProductsBulk,
    getProductStats,
    getProductsStats
}; 